// deps
import React, { memo, useContext, useMemo } from "react";

// internal-deps
import { ElementObserverOnceStart, useEffectOnce } from "@swiggy-private/core-ui";
import { ICouponCard } from "@swiggy-private/cart-types";
import { GroupOrderCartContext } from "@swiggy-private/checkout-core";
import { getDefaultSelectedCoupon, getDefaultSelectedOfferIdlist } from "@swiggy-private/cart-ui";
import { CouponCardGroup } from "@swiggy-private/core-ui";

// analytics
import { useCouponAnalytics } from "./hooks/useCouponAnalytics";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";
import { CouponRendererContext } from "@cart-client/contexts/CouponRendererContext";

// helpers
import { getCartId } from "@cart-client/helpers/Cart";
import { isPaymentOfferCouponGroup } from "@cart-client/helpers/CouponListing";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";
interface CouponsWrapperProps {
    card: ICouponCard;
}

const CouponsWrapper: React.FC<CouponsWrapperProps> = ({ card }) => {
    const { cartState } = useContext(CartContext);
    const { reviewedCart: cartResponse } = cartState;
    const { state: groupCartState } = useContext(GroupOrderCartContext);
    const {
        paymentOffersRef,
        couponSelections,
        onCouponCheckboxClick,
        handlers,
        isPaymentCouponListingPage,
        coupons,
        giftingAnalytics,
        isGiftingCouponListing,
    } = useContext(CouponRendererContext);

    const cartId = getCartId(cartResponse as ReviewedCart);
    const analytics = useCouponAnalytics(groupCartState, cartId);

    const commonCouponCardGroupProps = {
        key: card.type,
        couponGroup: card,
        onApplyCouponClick: handlers.applyCouponHandler,
        clickMoreDetailsEvent: analytics.handleClickMoreDetails,
        rewardOfferImpressionEvent: analytics.handleRewardOfferImpression,
        paymentBannerImpressionEvent: analytics.handlePaymentBannerImpression,
        onImpressionRemoveCoupon: analytics.handleImpressionRemoveCoupon,
        isPaymentCouponListingPage: isPaymentCouponListingPage.current,
        showTncCouponHalfcard: handlers.showTncCouponHalfcard,
    };

    const defaultSelectedOfferIdlist = useMemo(() => getDefaultSelectedOfferIdlist(coupons), [coupons]);
    const defaultSelectedCoupon = useMemo(() => getDefaultSelectedCoupon(coupons), [coupons]);

    useEffectOnce(() => {
        if (isGiftingCouponListing) {
            giftingAnalytics.handleImpressionOffers({
                defaultSelectedOfferIdlist,
                defaultSelectedCoupon,
            });
        }
    });

    const couponOffersImpressionEvent = useMemo(() => {
        return isGiftingCouponListing
            ? giftingAnalytics.handleCouponOffersImpression
            : analytics.handleCouponOffersImpression;
    }, [isGiftingCouponListing, giftingAnalytics, analytics]);

    return isPaymentOfferCouponGroup(card.type) ? (
        <ElementObserverOnceStart key={card.type} onViewEnter={handlers.onViewEnter}>
            <CouponCardGroup
                ref={paymentOffersRef}
                couponOffersImpressionEvent={analytics.handleCouponOffersImpression}
                {...commonCouponCardGroupProps}
            />
        </ElementObserverOnceStart>
    ) : (
        <CouponCardGroup
            {...commonCouponCardGroupProps}
            isGiftingCouponListing={isGiftingCouponListing}
            couponSelections={couponSelections}
            onCouponCheckboxClick={onCouponCheckboxClick}
            couponOffersImpressionEvent={couponOffersImpressionEvent}
        />
    );
};

export default memo(CouponsWrapper);
